/**
 * Goal Celebration — confetti + overlay when a goal is achieved.
 * Triggers canvas-confetti, shows celebration modal, auto-dismisses.
 */

import React, { useEffect, useCallback } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import confetti from 'canvas-confetti'
import { triggerHapticNotification as hapticNotification } from '@turnifly/shared/utils'
import type { Goal } from '@turnifly/shared/types'

interface GoalCelebrationProps {
  goal: Goal | null
  onDismiss: () => void
}

function fireConfetti(): void {
  const defaults = { startVelocity: 30, spread: 360, ticks: 60, zIndex: 200 }

  // First burst
  confetti({ ...defaults, particleCount: 80, origin: { x: 0.3, y: 0.6 } })
  confetti({ ...defaults, particleCount: 80, origin: { x: 0.7, y: 0.6 } })

  // Delayed second burst
  setTimeout(() => {
    confetti({ ...defaults, particleCount: 50, origin: { x: 0.5, y: 0.4 } })
  }, 300)
}

export const GoalCelebration: React.FC<GoalCelebrationProps> = ({ goal, onDismiss }) => {
  const { t } = useTranslation()

  useEffect(() => {
    if (!goal) return

    fireConfetti()
    hapticNotification('success')

    const timer = setTimeout(onDismiss, 4000)
    return () => clearTimeout(timer)
  }, [goal, onDismiss])

  const handleClick = useCallback((): void => {
    onDismiss()
  }, [onDismiss])

  return (
    <AnimatePresence>
      {goal && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          onClick={handleClick}
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm"
        >
          <motion.div
            initial={{ scale: 0.6, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.8, opacity: 0 }}
            transition={{ type: 'spring', damping: 15, stiffness: 200, delay: 0.1 }}
            onClick={(e) => e.stopPropagation()}
            className="mx-6 max-w-sm w-full rounded-3xl bg-surface border border-success/20 p-8 text-center shadow-[0_0_40px_rgba(34,197,94,0.15)]"
          >
            {/* Trophy */}
            <motion.div
              initial={{ scale: 0, rotate: -20 }}
              animate={{ scale: 1, rotate: 0 }}
              transition={{ type: 'spring', damping: 10, stiffness: 200, delay: 0.2 }}
              className="text-5xl mb-4"
            >
              🏆
            </motion.div>

            {/* Title */}
            <motion.h2
              initial={{ y: 10, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.35 }}
              className="text-lg font-display font-bold text-text mb-2"
            >
              {t('goals.celebrationTitle')}
            </motion.h2>

            {/* Goal name */}
            <motion.div
              initial={{ y: 10, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.45 }}
            >
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-2xl bg-success/[0.08] border border-success/[0.15]">
                <span className="text-lg">{goal.icon}</span>
                <span className="text-sm font-semibold text-success">
                  {goal.is_private ? t('goals.privateGoal') : goal.name}
                </span>
              </div>
            </motion.div>

            {/* Stats */}
            <motion.p
              initial={{ y: 10, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.55 }}
              className="text-xs text-text-hint mt-3"
            >
              {goal.goal_type === 'steps'
                ? t('goals.stepsProgress', {
                    current: goal.current_value,
                    total: goal.steps?.length ?? goal.target_value,
                    percent: 100,
                  })
                : t('goals.celebrationStats', {
                    current: goal.current_value,
                    target: goal.target_value,
                    unit: goal.unit,
                  })}
            </motion.p>

            {/* Dismiss hint */}
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 0.5 }}
              transition={{ delay: 1.5 }}
              className="text-[10px] text-text-hint mt-6"
            >
              {t('goals.celebrationDismiss')}
            </motion.p>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}
